import { useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "../components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../lib/utils";

// Normalise a question's options into [{value,label}] rows. Prefer the
// admin-driven `options_data` (value may differ from label, e.g. currencies);
// fall back to plain string `options`.
function optionRows(q) {
  if (Array.isArray(q.options_data) && q.options_data.length) {
    return q.options_data.map((o) =>
      typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value }
    );
  }
  return (q.options || []).map((o) => ({ value: o, label: o }));
}

function SearchableSelect({ tid, rows, value, placeholder, disabled, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = rows.find((r) => r.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={tid}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
            "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            !selected && "text-muted-foreground"
          )}
        >
          <span className="truncate">{selected ? selected.label : (placeholder || "Select…")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" data-testid={`${tid}-search`} />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {rows.map((r) => (
                <CommandItem
                  key={r.value}
                  value={r.label}
                  data-testid={`${tid}-opt-${String(r.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onSelect={() => { onChange(r.value); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === r.value ? "opacity-100" : "opacity-0")} />
                  {r.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function SmartInspectField({ q, value, onChange, error, disabled, currency, isDefault }) {
  const tid = `si-field-${q.field_key}`;
  const common = { "data-testid": tid, disabled };
  const unitText = q.input_type === "currency" ? (currency || "USD") : q.unit;
  const label = (
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {q.label}{q.required ? <span className="text-red-500"> *</span> : null}
      {unitText ? <span className="text-slate-400 font-normal"> ({unitText})</span> : null}
    </label>
  );

  let control;
  switch (q.input_type) {
    case "textarea":
      control = <Textarea {...common} value={value ?? ""} placeholder={q.placeholder} onChange={(e) => onChange(e.target.value)} />;
      break;
    case "searchable-select":
      control = (
        <SearchableSelect
          tid={tid}
          rows={optionRows(q)}
          value={value ?? ""}
          placeholder={q.placeholder}
          disabled={disabled}
          onChange={onChange}
        />
      );
      break;
    case "dropdown":
    case "radio":
      control = (
        <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger data-testid={tid}><SelectValue placeholder={q.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent>
            {optionRows(q).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
      break;
    case "yesno":
      control = (
        <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      );
      break;
    case "number":
    case "currency":
    case "percentage":
      control = (
        <div className="relative">
          <Input {...common} type="number" value={value ?? ""} placeholder={q.placeholder}
            min={q.min_value ?? undefined} max={q.max_value ?? undefined}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
          {q.input_type === "percentage" ? <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span> : null}
        </div>
      );
      break;
    default:
      control = <Input {...common} value={value ?? ""} placeholder={q.placeholder} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <div>
      {label}
      {control}
      {q.help_text ? <p className="text-xs text-slate-400 mt-1">{q.help_text}</p> : null}
      {error ? <p data-testid={`si-error-${q.field_key}`} className="text-xs text-red-600 mt-1">{error}</p> : null}
    </div>
  );
}
